package co.maytes.standupbot.dashboard.components.marketing

import co.maytes.standupbot.dashboard.components.widgets.SectionStatus
import co.maytes.standupbot.dashboard.components.widgets.StandupUpdate

enum class DemoStepState { Pending, Active, Done }

data class DemoStep(val label: String, val state: DemoStepState)

data class DemoPerson(
    val name: String,
    val yesterday: String,
    val today: String,
    val blockers: String? = null,
)

data class DemoFrame(
    val steps: List<DemoStep>,
    val updates: List<StandupUpdate>,
    val windowLive: Boolean,
    val actionsVisible: Boolean,
    val ackPressed: Boolean,
    val approved: Boolean,
    val finished: Boolean,
)

object StandupDemoScript {
    const val CARET = "▍"
    const val CHAR_MS = 9L
    const val GITHUB_AT_MS = 700L
    const val CLAUDE_AT_MS = 1_400L
    const val POST_LAG_MS = 450L
    const val PRESS_AFTER_MS = 900L
    const val PRESS_HOLD_MS = 240L
    const val SETTLE_MS = 500L
    const val APPROVED_BADGE = "✓ approved"
    const val EDITED_BADGE = "✎ edited"

    val stepLabels = listOf("Reads Linear", "Reads GitHub", "Claude writes", "Posts to #standup")

    val people = listOf(
        DemoPerson(
            name = "maya",
            yesterday = "Shipped OAuth refresh-token rotation (STB-421). Reviewed 3 PRs on the scheduler.",
            today = "Wiring Quartz cron UI into standup settings, pairing with Sam at 11.",
            blockers = "Waiting on design for the empty state. Nudged Priya.",
        ),
        DemoPerson(
            name = "jordan",
            yesterday = "Landed Linear webhook dedupe (STB-398). Closed 2 flaky scheduler tests.",
            today = "Rolling out the activity-feed index behind a feature flag.",
        ),
        DemoPerson(
            name = "priya",
            yesterday = "Finalized dashboard onboarding wireframes. Sign-off from Max.",
            today = "Prototyping the schedule-builder drag interaction in Figma.",
        ),
    )

    private val totalChars = people.sumOf { it.yesterday.length + it.today.length + (it.blockers?.length ?: 0) }

    val typingEndMs = CLAUDE_AT_MS + totalChars * CHAR_MS
    val actionsAtMs = typingEndMs + POST_LAG_MS
    val pressAtMs = actionsAtMs + PRESS_AFTER_MS
    val approvedAtMs = pressAtMs + PRESS_HOLD_MS
    val durationMs = approvedAtMs + SETTLE_MS

    private val stepStarts = listOf(0L, GITHUB_AT_MS, CLAUDE_AT_MS, typingEndMs)
    private val stepEnds = listOf(GITHUB_AT_MS, CLAUDE_AT_MS, typingEndMs, actionsAtMs)

    fun frame(elapsedMs: Long): DemoFrame =
        DemoFrame(
            steps = stepLabels.mapIndexed { index, label -> DemoStep(label, stepState(index, elapsedMs)) },
            updates = typedUpdates(((elapsedMs - CLAUDE_AT_MS).coerceAtLeast(0L) / CHAR_MS).toInt()),
            windowLive = elapsedMs >= CLAUDE_AT_MS,
            actionsVisible = elapsedMs >= actionsAtMs,
            ackPressed = elapsedMs in pressAtMs until approvedAtMs,
            approved = elapsedMs >= approvedAtMs,
            finished = elapsedMs >= durationMs,
        )

    fun displayedUpdates(frame: DemoFrame, userStatus: SectionStatus?): List<StandupUpdate> {
        val status = userStatus ?: if (frame.approved) SectionStatus.Approved else SectionStatus.Generated
        return frame.updates.mapIndexed { index, update ->
            if (index == 0 && status != SectionStatus.Generated) {
                update.copy(status = status, acknowledgedBadge = badgeFor(status))
            } else {
                update
            }
        }
    }

    fun completeUpdates(): List<StandupUpdate> = typedUpdates(Int.MAX_VALUE)

    private fun badgeFor(status: SectionStatus): String? =
        when (status) {
            SectionStatus.Approved -> APPROVED_BADGE
            SectionStatus.Edited -> EDITED_BADGE
            else -> null
        }

    private fun stepState(index: Int, elapsedMs: Long): DemoStepState =
        when {
            elapsedMs < stepStarts[index] -> DemoStepState.Pending
            elapsedMs < stepEnds[index] -> DemoStepState.Active
            else -> DemoStepState.Done
        }

    private fun typedUpdates(typedChars: Int): List<StandupUpdate> {
        var remaining = typedChars
        val updates = mutableListOf<StandupUpdate>()
        for (person in people) {
            if (remaining <= 0) break
            val yesterday = typed(person.yesterday, remaining)
            remaining = yesterday.remaining
            val today = typed(person.today, remaining)
            remaining = today.remaining
            val blockers = person.blockers?.let { typed(it, remaining) }
            remaining = blockers?.remaining ?: remaining
            updates += StandupUpdate(
                person = person.name,
                yesterday = yesterday.text,
                today = today.text,
                blockers = blockers?.text?.takeIf { it.isNotEmpty() },
            )
        }
        return updates
    }

    private class Typed(val text: String, val remaining: Int)

    private fun typed(text: String, remaining: Int): Typed =
        when {
            remaining <= 0 -> Typed("", 0)
            remaining >= text.length -> Typed(text, remaining - text.length)
            else -> Typed(text.take(remaining) + CARET, 0)
        }
}
